import type { ComponentProps } from 'react';
import { Button } from '@herit-fe/heritage-ui';
import { ListRestart, MoreHorizontal, SlidersHorizontal, type LucideIcon } from 'lucide-react';
import { UI_TEXT } from '@/shared/languages';
import { ActionMenu, type ActionMenuItem } from './action-menu';
import { useIsCompact } from './use-is-compact';

/** 툴바 항목 1개 — a(페이지 헤더)·c(필터 행)·d(카드 헤더) 공통. non-pc=아이콘만 / pc=라벨+아이콘. */
export interface ToolbarAction {
  key: string;
  label: string;
  icon: LucideIcon;
  /** 단일 액션 클릭 핸들러. `items`(드롭다운) 액션이면 불필요. */
  onClick?: () => void;
  variant?: ComponentProps<typeof Button>['variant'];
  disabled?: boolean;
  /** 비활성 등 사유 툴팁(native title) — 예: 다운로드 비활성 사유(왜 못 누르는지 사전 안내). */
  title?: string;
  /** 파괴적 액션(삭제 등) — overflow 메뉴에서 destructive 스타일. */
  destructive?: boolean;
  /**
   * 있으면 이 액션은 **드롭다운**(`ActionMenu`) — 단일 버튼 대신 하위 항목 메뉴로 렌더(onClick 무시).
   * 연관 액션을 한 트리거로 묶을 때(예: 다운로드 ▾ → 엑셀·캡쳐). overflow 로 접힐 땐 하위 항목이 ⋯ 로 펼쳐진다.
   */
  items?: ActionMenuItem[];
}

/** 조회조건 트리거 기술자 — Toolbar 가 배지 포함 트리거를 직접 렌더(슬롯 1 카운트). */
export interface ToolbarFilter {
  /** 활성 조건 수(배지). */
  activeCount: number;
  /** 시트 열기. */
  onOpen: () => void;
  /** 트리거 라벨(기본 조회조건). */
  label?: string;
}

export interface ToolbarProps {
  /** [초기화](선택) — ghost. **개수(max)에서 제외**. 화면이 변경 있을 때만 넘긴다(없으면 미표시). */
  reset?: () => void;
  /** [조회조건] 트리거(선택) — 있으면 항상 첫 슬롯, **개수 1**. */
  filter?: ToolbarFilter;
  /** 명령형/조회 액션 — 다운로드·갱신·등록·캡쳐 등. 한도 초과분은 자동으로 ⋯ 메뉴로 접힘. */
  actions?: ToolbarAction[];
  /** 초기화 제외 최대 노출 슬롯(필터 트리거 포함). 기본 2. */
  max?: number;
  /** overflow(⋯) 트리거 라벨(기본 더보기). 목록은 '관리' 등으로 재정의. */
  overflowLabel?: string;
  /** overflow(⋯) 트리거 아이콘(기본 MoreHorizontal). */
  overflowIcon?: LucideIcon;
}

/**
 * 우측 제어 묶음 정규화 — **[초기화(개수 제외)] + [조회조건(개수 1)] + [액션 or ⋯]**. 초기화 제외 최대 `max`(기본 2)
 * 를 넘는 액션은 마지막 슬롯의 ⋯ `ActionMenu` 로 자동 접는다("필터+액션 2개 이상 → 반드시 ⋯" 불변식). 반응형은
 * `useIsCompact`(lg=1024) — non-pc=아이콘만 / pc=라벨+아이콘.
 *
 * 배치처: **a**=`PageHeader.actions`(헤더 trailing 포탈) · **c**=`FilterBar` 트레일링 · **d**=`RegionCard.action`.
 * 담는 것이 액션만이 아니라(조회조건 트리거·초기화 포함) 이름은 도구 묶음(toolbar) 기준 — 배치 영역과 무관하게 동일 API.
 *
 * ⚠️ ARIA `role="toolbar"` 는 **의도적으로 미부착** — 그 role 은 화살표키 로빙 포커스(roving tabindex)를 계약으로
 * 요구한다. 구현 없이 role 만 붙이면 스크린리더 사용자가 화살표를 눌러도 이동이 안 돼 오히려 손해다. 로빙 포커스를
 * 구현하는 시점에 함께 붙일 것.
 */
export function Toolbar({
  reset,
  filter,
  actions = [],
  max = 2,
  overflowLabel,
  overflowIcon,
}: ToolbarProps) {
  const isCompact = useIsCompact();

  // 필터 트리거가 슬롯 1을 먹으면 액션 예산은 max-1. 예산 초과 시 마지막 슬롯을 ⋯ 로 확보(inline=예산-1).
  const budget = Math.max(0, max - (filter ? 1 : 0));
  const willOverflow = actions.length > budget;
  const inline = willOverflow ? actions.slice(0, Math.max(0, budget - 1)) : actions;
  const overflow = willOverflow ? actions.slice(Math.max(0, budget - 1)) : [];

  const badge =
    filter && filter.activeCount > 0 ? (
      <span className="absolute -right-1 -top-1 flex size-4 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground">
        {filter.activeCount}
      </span>
    ) : null;

  // 드롭다운 액션(items)이 overflow 로 접히면 하위 항목을 그대로 ⋯ 에 펼친다(중첩 메뉴 회피). 단일 액션은 항목 1개로.
  const overflowItems: ActionMenuItem[] = overflow.flatMap(
    (a) =>
      a.items ?? [
        {
          key: a.key,
          label: a.label,
          icon: a.icon,
          onClick: a.onClick ?? (() => undefined),
          disabled: a.disabled,
          destructive: a.destructive,
        },
      ],
  );

  return (
    <div className="flex items-center gap-1">
      {/* 초기화 — ghost, 개수 제외. non-pc 아이콘만 / pc 텍스트+아이콘. */}
      {reset ? (
        isCompact ? (
          <Button variant="ghost" size="icon" aria-label={UI_TEXT.FILTER_RESET} onClick={reset}>
            <ListRestart />
          </Button>
        ) : (
          <Button variant="ghost" onClick={reset} className="gap-2">
            {UI_TEXT.FILTER_RESET}
            <ListRestart />
          </Button>
        )
      ) : null}

      {/* 조회조건 트리거(배지) — outline. */}
      {filter ? (
        isCompact ? (
          <Button
            variant="outline"
            size="icon"
            aria-label={filter.label ?? UI_TEXT.FILTER_TITLE}
            onClick={filter.onOpen}
            className="relative"
          >
            <SlidersHorizontal />
            {badge}
          </Button>
        ) : (
          <Button variant="outline" onClick={filter.onOpen} className="relative gap-2">
            {filter.label ?? UI_TEXT.FILTER_TITLE}
            <SlidersHorizontal />
            {badge}
          </Button>
        )
      ) : null}

      {/* 인라인 액션 — 드롭다운(items)이면 ActionMenu, 아니면 non-pc 아이콘만 / pc 라벨+아이콘 버튼. */}
      {inline.map((a) => {
        const Icon = a.icon;
        if (a.items) {
          return <ActionMenu key={a.key} label={a.label} icon={a.icon} items={a.items} />;
        }
        return isCompact ? (
          <Button
            key={a.key}
            variant={a.destructive ? 'destructive' : (a.variant ?? 'outline')}
            size="icon"
            aria-label={a.label}
            title={a.title ?? a.label}
            disabled={a.disabled}
            onClick={a.onClick}
          >
            <Icon />
          </Button>
        ) : (
          <Button
            key={a.key}
            variant={a.destructive ? 'destructive' : (a.variant ?? 'outline')}
            title={a.title}
            disabled={a.disabled}
            onClick={a.onClick}
            className="gap-2"
          >
            {a.label}
            <Icon />
          </Button>
        );
      })}

      {/* overflow(⋯) — 한도 초과 액션 묶음. */}
      {overflowItems.length > 0 ? (
        <ActionMenu
          label={overflowLabel ?? UI_TEXT.COMMON_MORE}
          icon={overflowIcon ?? MoreHorizontal}
          items={overflowItems}
        />
      ) : null}
    </div>
  );
}
